import "./App.css";
import React, { lazy, Suspense, memo } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useAuth } from "./components/AuthRouter/AuthContext";
import { DecryptToken } from "./helper/Constants.js";
import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import 'antd/dist/antd.css';

// Eager loading for critical components
import Layout from "./components/boilerPlat/layout";
import PBXTaskManager from "./PBXTaskManager";

// Lazy load all route components
const Dashboard = lazy(() => import("./components/Dashboard"));
const UserProfile = lazy(() => import("./components/UserProfile"));
const Purchaseplan = lazy(() => import("./components/Purchaseplan"));
const NeedHelp = lazy(() => import("./components/NeedHelp"));
const LiveSummary = lazy(() => import("./components/LiveSummary"));
const BlockedNumbers = lazy(() => import("./lists/BlockedNumbers"));
const CreateCampaigns = lazy(() => import("./components/CreateCampaigns"));
const ManageCampaigns = lazy(() => import("./components/ManageCampaigns"));
const ManageCampaignsCalls = lazy(() => import("./components/ManageCampaignsCalls"));
const ManageCampaignsHistory = lazy(() => import("./components/ManageCampaignsHistory"));
const Publishers = lazy(() => import("./components/Publishers"));
const ManageBuyers = lazy(() => import("./components/Targets-pages/ManageBuyers"));
const CreateTargets = lazy(() => import("./components/Targets-pages/CreateTargets"));
const ManageTargets = lazy(() => import("./components/Targets-pages/ManageTargets"));
const ManageGroups = lazy(() => import("./components/Targets-pages/ManageGroups"));
const Sucess = lazy(() => import("./components/Sucess"));
const Register = lazy(() => import("./components/Register"));
const ReportList = lazy(() => import("./lists/ReportList"));
const PurchaseNumberList = lazy(() => import("./lists/PurchaseNumberList"));
const AddPurchaseNumber = lazy(() => import("./lists/AddPurchaseNumber"));
const Support = lazy(() => import("./components/Support"));
const EditCampaign = lazy(() => import("./components/EditCampaign"));
const AddNumberCampaign = lazy(() => import("./components/AddNumberCampaign"));
const IpAuth = lazy(() => import("./components/IpAuth"));
const NumberRouting = lazy(() => import("./components/NumberRouting"));

// SMS Components
const Compose = lazy(() => import("./components/SMS/Compose"));
const BulksmsRange = lazy(() => import("./components/SMS/BulksmsRange"));
const AddContact = lazy(() => import("./components/SMS/AddContact"));
const DynamiceSMS = lazy(() => import("./components/SMS/DynamiceSMS"));
const BulkSchedlingsms = lazy(() => import("./components/SMS/BulkSchedlingsms"));
const DBCampaign = lazy(() => import("./components/SMS/DBCampaign"));
const DLRReport = lazy(() => import("./components/SMS/DLRReport"));
const RefundReport = lazy(() => import("./components/SMS/RefundReport"));
const SMSTemplates = lazy(() => import("./components/SMS/SMSTemplates"));
const ManageSenders = lazy(() => import("./components/SMS/ManageSenders"));
const ManageTemplates = lazy(() => import("./components/SMS/ManageTemplates"));
const PricingCoverage = lazy(() => import("./components/SMS/PricingCoverage"));
const BulkSMS = lazy(() => import("./components/SMS/BulkSMS"));
const ContactDetails = lazy(() => import("./components/SMS/ContactDetails"));

// WhatsApp Components
const SendMessagesReport = lazy(() => import("./components/Whatsapp/Report/SendMessagesReport"));
const ReceivedMessagesReport = lazy(() => import("./components/Whatsapp/Report/ReceivedMessagesReport"));
const AutoReply = lazy(() => import("./components/Whatsapp/AutoReply"));
const Device = lazy(() => import("./components/Whatsapp/Device"));
const SendMessages = lazy(() => import("./components/Whatsapp/SendMessages"));
const Templates = lazy(() => import("./components/Whatsapp/Templates"));
const WelcomeTemplates = lazy(() => import("./components/Whatsapp/WelcomeTemplate"));
const DBCampaignApp = lazy(() => import("./components/WhatsappCampaign/DBCampaignApp"));
const InternationCampaign = lazy(() => import("./components/WhatsappCampaign/InternationCampaign"));
const CampaignReport = lazy(() => import("./components/WhatsappCampaign/CampaignReport"));

// Phone Components
const Dialpad = lazy(() => import("./components/phone/Dialpad"));
const LiveCallsReporting = lazy(() => import("./components/phone/LiveCalls"));
const Callhistory = lazy(() => import("./components/phone/Callhistory"));
const Calls = lazy(() => import("./components/phone/Calls"));
const ManageUsers = lazy(() => import("./components/phone/ManageUsers"));
const RingGroup = lazy(() => import("./components/phone/RingGroup"));

// Voice Components
const Callgreetings = lazy(() => import("./components/Voice/Callgreetings"));
const IvrService = lazy(() => import("./components/Voice/IvrService"));

// Auth Components
const ForgotPassword = lazy(() => import("./components/Login/ForgotPassword"));
const Logs = lazy(() => import("./components/Login/Logs"));
const Resetpasword = lazy(() => import("./components/Resetpassword"));
const VerifyEmail = lazy(() => import("./components/VerifyEmail"));
const Wallet = lazy(() => import("./components/Wallet"));
const VoipBlock = lazy(() => import("./components/Voipblock"));
// Loading fallback component
const LoadingFallback = memo(() => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    fontSize: '18px',
    color: '#666'
  }}>
    Loading...
  </div>
));

LoadingFallback.displayName = 'LoadingFallback';

// Memoized Layout wrapper
const RenderLayout = memo(({ children }) => <Layout>{children}</Layout>);
RenderLayout.displayName = 'RenderLayout';

// Memoized Private Route component
const PrivateRoute = memo(() => {
  const { loggedIn } = useAuth();
  return loggedIn ? <Outlet /> : <Navigate to="/" replace />;
});
PrivateRoute.displayName = 'PrivateRoute';

function App() {
  // Move token decryption outside component if possible, or memoize
  const userToken = React.useMemo(() => localStorage.getItem("psx_token"), []);
  const decodedToken = React.useMemo(() => DecryptToken(userToken), [userToken]);

  return (
    <div className="App">
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Logs />} />
            <Route path="/forget_password" element={<ForgotPassword />} />
            <Route path="/resetpassword" element={<Resetpasword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/pbx-manager" element={<PBXTaskManager />} />

            {/*  */}

            {/* Protected Routes */}
            <Route element={<PrivateRoute />}>
              {/* Dashboard */}
              <Route path="/dashboard" element={<RenderLayout><Dashboard /></RenderLayout>} />
              <Route path="/admin-dashboard" element={<RenderLayout><Dashboard /></RenderLayout>} />

              {/* Profile & Settings */}
              <Route path="/profile" element={<RenderLayout><UserProfile /></RenderLayout>} />
              <Route path="/purchaseplan" element={<RenderLayout><Purchaseplan /></RenderLayout>} />
              <Route path="/success" element={<RenderLayout><Sucess /></RenderLayout>} />
              <Route path="/need-help" element={<RenderLayout><NeedHelp /></RenderLayout>} />

              {/* Calls & Reports */}
              <Route path="/live-calls" element={<RenderLayout><LiveSummary /></RenderLayout>} />
              <Route path="/report-list" element={<RenderLayout><ReportList /></RenderLayout>} />

              {/* Number Management */}
              <Route path="/purchase-number-list" element={<RenderLayout><PurchaseNumberList /></RenderLayout>} />
              <Route path="/add-purchase-number" element={<RenderLayout><AddPurchaseNumber /></RenderLayout>} />
              <Route path="/block-number" element={<RenderLayout><BlockedNumbers /></RenderLayout>} />

              {/* Campaigns */}
              <Route path="/create-campaigns" element={<RenderLayout><CreateCampaigns /></RenderLayout>} />
              <Route path="/manage-campaigns" element={<RenderLayout><ManageCampaigns /></RenderLayout>} />
              <Route path="/campaigns-calls" element={<RenderLayout><ManageCampaignsCalls /></RenderLayout>} />
              <Route path="/campaigns-history" element={<RenderLayout><ManageCampaignsHistory /></RenderLayout>} />
              <Route path="/edit-campaign/:id" element={<RenderLayout><EditCampaign /></RenderLayout>} />
              <Route path="/add-number-campaign" element={<RenderLayout><AddNumberCampaign /></RenderLayout>} />

              {/* Publishers & Targets */}
              <Route path="/publishers" element={<RenderLayout><Publishers /></RenderLayout>} />
              <Route path="/manage-buyers" element={<RenderLayout><ManageBuyers /></RenderLayout>} />
              <Route path="/create-targets" element={<RenderLayout><CreateTargets /></RenderLayout>} />
              <Route 
                path="/manage-targets" 
                element={<RenderLayout><ManageTargets user_id={decodedToken?.user_id || null} /></RenderLayout>} 
              />
              <Route path="/manage-groups" element={<RenderLayout><ManageGroups /></RenderLayout>} />

              {/* Authentication & Routing */}
              <Route path="/ip-authentication" element={<RenderLayout><IpAuth /></RenderLayout>} />
              <Route path="/number-routing" element={<RenderLayout><NumberRouting /></RenderLayout>} />

              {/* SMS Routes */}
              <Route path="/compose" element={<RenderLayout><Compose /></RenderLayout>} />
              <Route path="/AddContact" element={<RenderLayout><AddContact /></RenderLayout>} />
              <Route path="/bulk-sms" element={<RenderLayout><BulkSMS /></RenderLayout>} />
              <Route path="/bulksms-range" element={<RenderLayout><BulksmsRange /></RenderLayout>} />
              <Route path="/dynaminc-sms" element={<RenderLayout><DynamiceSMS /></RenderLayout>} />
              <Route path="/bulk-scheduling" element={<RenderLayout><BulkSchedlingsms /></RenderLayout>} />
              <Route path="/db-camaign" element={<RenderLayout><DBCampaign /></RenderLayout>} />
              <Route path="/dlr-report" element={<RenderLayout><DLRReport /></RenderLayout>} />
              <Route path="/refund-report" element={<RenderLayout><RefundReport /></RenderLayout>} />
              <Route path="/sms-templates" element={<RenderLayout><SMSTemplates /></RenderLayout>} />
              <Route path="/manage-senders" element={<RenderLayout><ManageSenders /></RenderLayout>} />
              <Route path="/manage-templates" element={<RenderLayout><ManageTemplates /></RenderLayout>} />
              <Route path="/pricing-coverage" element={<RenderLayout><PricingCoverage /></RenderLayout>} />
              <Route path="/contact-details" element={<RenderLayout><ContactDetails /></RenderLayout>} />

              {/* WhatsApp Routes */}
              <Route path="/send-messages-report" element={<RenderLayout><SendMessagesReport /></RenderLayout>} />
              <Route path="/received-messages-report" element={<RenderLayout><ReceivedMessagesReport /></RenderLayout>} />
              <Route path="/auto-reply" element={<RenderLayout><AutoReply /></RenderLayout>} />
              <Route path="/device" element={<RenderLayout><Device /></RenderLayout>} />
              <Route path="/send-messages" element={<RenderLayout><SendMessages /></RenderLayout>} />
              <Route path="/templates" element={<RenderLayout><Templates /></RenderLayout>} />
              <Route path="/welcome-template" element={<RenderLayout><WelcomeTemplates /></RenderLayout>} />
              <Route path="/db-campaignapp" element={<RenderLayout><DBCampaignApp /></RenderLayout>} />
              <Route path="/internation-campaign" element={<RenderLayout><InternationCampaign /></RenderLayout>} />
              <Route path="/campaign-report" element={<RenderLayout><CampaignReport /></RenderLayout>} />

              {/* Phone Routes */}
              <Route path="/dial-pad" element={<RenderLayout><Dialpad /></RenderLayout>} />
              <Route path="/live-calls-reporting" element={<RenderLayout><LiveCallsReporting /></RenderLayout>} />
              <Route path="/calls" element={<RenderLayout><Calls /></RenderLayout>} />
              <Route path="/call-history" element={<RenderLayout><Callhistory /></RenderLayout>} />
              <Route path="/manage-users" element={<RenderLayout><ManageUsers /></RenderLayout>} />
              <Route path="/ring-group" element={<RenderLayout><RingGroup /></RenderLayout>} />

              {/* Voice Routes */}
              <Route path="/call-greetings" element={<RenderLayout><Callgreetings /></RenderLayout>} />
              <Route path="/ivr-service" element={<RenderLayout><IvrService /></RenderLayout>} />
              <Route path="/voip-block" element={<RenderLayout><VoipBlock /></RenderLayout>} />

              {/* Support */}
              <Route path="/support" element={<RenderLayout><Support /></RenderLayout>} />
            </Route>

            {/* Wallet - Outside protected routes as per original */}
            <Route path="/wallet" element={<RenderLayout><Wallet /></RenderLayout>} />
          </Routes>
        </Suspense>

        <ToastContainer/>
      </Router>
    </div>
  );
}

export default memo(App);